import React, { useEffect, useState, useMemo } from 'react';
import api from '../api/client';
import { Activity, Handshake, AlertTriangle, Flag, ShieldCheck, Calendar, FileText, Loader2, Filter } from 'lucide-react';
import SearchBar, { matches } from '../components/app/SearchBar';

const KIND_META = {
  negotiation: { label: 'Negociación', icon: Handshake, color: 'text-blue-300', bg: 'bg-blue-500/10 border-blue-500/30' },
  incident:    { label: 'Incidencia',  icon: AlertTriangle, color: 'text-red-300',  bg: 'bg-red-500/10 border-red-500/30' },
  conflict:    { label: 'Conflictivo', icon: Flag,          color: 'text-orange-300', bg: 'bg-orange-500/10 border-orange-500/30' },
  preventive:  { label: 'Preventivo',  icon: ShieldCheck,   color: 'text-green-300', bg: 'bg-green-500/10 border-green-500/30' },
  agenda:      { label: 'Agenda',      icon: Calendar,      color: 'text-purple-300', bg: 'bg-purple-500/10 border-purple-500/30' },
  normativa:   { label: 'Normativa',   icon: FileText,      color: 'text-[#A3FF12]', bg: 'bg-[#A3FF12]/10 border-[#A3FF12]/30' },
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Eventos = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKinds, setSelectedKinds] = useState(new Set(['negotiation', 'incident', 'conflict', 'preventive', 'agenda', 'normativa']));
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const kinds = Array.from(selectedKinds).join(',');
      const { data } = await api.get('/events', { params: { kinds, limit: 200 } });
      setEvents(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [selectedKinds]); // eslint-disable-line

  const toggleKind = (k) => {
    const s = new Set(selectedKinds);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSelectedKinds(s);
  };

  // group by day
  const grouped = useMemo(() => {
    const g = {};
    events
      .filter((e) => matches(search, e.title, e.message, e._site_name, e._site_municipality, e.status, e.priority))
      .forEach((e) => {
        const day = (e.date || '').slice(0, 10) || 'Sin fecha';
        if (!g[day]) g[day] = [];
        g[day].push(e);
      });
    return Object.entries(g).sort((a, b) => (b[0] < a[0] ? -1 : 1));
  }, [events, search]);

  const counts = useMemo(() => {
    const c = {};
    events.forEach((e) => { c[e.kind] = (c[e.kind] || 0) + 1; });
    return c;
  }, [events]);

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="events-page">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-[#A3FF12]">SEGUIMIENTO</div>
        <h1 className="text-3xl sm:text-4xl font-black mt-1">Timeline de eventos</h1>
        <p className="text-white/50 text-sm mt-1">Todo lo que ocurre en la operación — negociaciones, incidencias, conflictivos, preventivo, normativa y agenda.</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Filter size={14} className="text-white/40"/>
        {Object.entries(KIND_META).map(([k, m]) => {
          const active = selectedKinds.has(k);
          const Icon = m.icon;
          return (
            <button key={k} onClick={() => toggleKind(k)} data-testid={`filter-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                active ? m.bg + ' ' + m.color : 'bg-white/5 border-white/10 text-white/40 hover:text-white/70'
              }`}>
              <Icon size={12}/> {m.label}
              {counts[k] > 0 && <span className="text-[10px] opacity-70">({counts[k]})</span>}
            </button>
          );
        })}
        <div className="flex-1 min-w-[200px] max-w-md ml-auto">
          <SearchBar value={search} onChange={setSearch} placeholder="Buscar por título, site o mensaje…" testId="events-search"/>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
      ) : events.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl">
          <Activity className="mx-auto text-white/20 mb-2" size={40}/>
          <p className="text-white/50">Sin eventos que mostrar.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([day, dayEvents]) => (
            <div key={day}>
              <div className="sticky top-0 bg-black/80 backdrop-blur py-1 mb-2 flex items-center gap-2 z-10">
                <span className="text-xs font-black uppercase tracking-widest text-white/50">
                  {day === 'Sin fecha' ? day : new Date(day + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <span className="text-[10px] text-white/30">· {dayEvents.length} eventos</span>
                <div className="flex-1 border-t border-white/10 ml-2"/>
              </div>
              <div className="space-y-2 border-l-2 border-white/10 pl-4">
                {dayEvents.map((e, i) => {
                  const meta = KIND_META[e.kind] || KIND_META.negotiation;
                  const Icon = meta.icon;
                  return (
                    <div key={`${e.kind}-${e.ref_id}-${i}`} data-testid={`event-${e.kind}`}
                      className={`relative bg-black/30 border ${meta.bg.split(' ')[1]} rounded-xl p-3 hover:border-[#A3FF12]/30`}>
                      <div className={`absolute -left-[27px] top-4 w-3 h-3 rounded-full ${meta.bg.split(' ')[0]} border-2 border-black`}/>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color} border`}><Icon size={10}/> {meta.label}</span>
                            {e.priority && <span className="text-[10px] uppercase text-white/50">{e.priority}</span>}
                            {e.status && <span className="text-[10px] text-white/50">{e.status}</span>}
                          </div>
                          <div className="text-white font-bold text-sm mt-1">{e.title}</div>
                          {e.message && <div className="text-white/60 text-xs mt-1">{e.message}</div>}
                          <div className="text-white/40 text-[10px] mt-1 flex gap-3 flex-wrap">
                            {e._site_name && <span>📍 {e._site_name}{e._site_municipality ? ` · ${e._site_municipality}` : ''}</span>}
                            <span>{fmtDate(e.date)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Eventos;
